/// <amd-module name="Wit/QueriesHub/QueryList/QueryListViewComponent" />

import * as React from "react";
import {
    IObservableArray,
    IObservableValue,
    IReadonlyObservableArray,
    ObservableArray,
    ObservableCollection,
    ObservableValue
} from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { onClickFPS } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { Components, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { FilterBar } from "VSSUI/FilterBar";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IObservableExpression, Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBar, TabContent, TabSize } from "VSSUI/Tabs";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { FILTER_CHANGE_EVENT, Filter } from "VSSUI/Utilities/Filter";
import { QueryHierarchyItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature } from "Wit/Common/Constants/CustomerInteligence";
import { IQueryListService } from "Wit/Common/QueryListServices/IQueryListService";
import { QuerySaveDialog } from "Wit/Common/QuerySaveDialog/Components/QuerySaveDialog";
import { IQuerySaveDialogProps } from "Wit/Common/QuerySaveDialog/Components/QuerySaveDialog.Props";
import { ObservableFavoriteItemsGroup, QueryItem } from "Wit/Common/QueryUtils/Models";
import * as QueriesHubConstants from "Wit/Common/QueryUtils/QueriesHubConstants";
import { getDeletedUrl, getImportWorkItemsUrl } from "Wit/Common/QueryUtils/RoutingUtils";
import * as Resources from "Wit/QueriesHub/Resources";
import { QueryBreadcrumb } from "../Common/Components/QueryBreadcrumb";
import { AllQueriesTab } from "./Components/AllQueriesTab";
import { FavoriteQueriesTab } from "./Components/FavoriteQueriesTab";
import { FilteredQueriesTab } from "./Components/FilteredQueryTab";

export interface IQueryListViewContentProps {}

export interface IQueriesListViewState {
    tabs: IReadonlyObservableArray<IVssContributedTab>;
}

export class QueryListViewComponent extends VssComponent<IQueryListViewContentProps, IQueriesListViewState> {
    private _allUrl: string;
    private _commands: IObservableArray<IHeaderCommandBarItem> = new ObservableArray(undefined);
    private _favoritesUrl: string;
    private _filter: Filter;
    private _filteredQueryItems: ObservableArray<QueryItem> = new ObservableArray([]);
    private _filteredQueryGroupItems: ObservableArray<ObservableFavoriteItemsGroup> = new ObservableArray([]);
    private _folderQueryItems: QueryItem[] = [];
    private _hasMoreResults: ObservableValue<boolean> = new ObservableValue(false);
    private _isFolderView: boolean;
    private _isFiltered: ObservableValue<boolean> = new ObservableValue(false);
    private _isLoading: ObservableValue<boolean> = new ObservableValue(false);
    private _layoutManager: IVssLayoutManager;
    private _listService: IQueryListService;
    private _pivot: IObservableValue<string>;
    private _shortcutService: IVssKeyboardShortcutService;

    constructor(props: IQueryListViewContentProps, context: IVssComponentContext) {
        super(props, context);

        this._shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        this._favoritesUrl = historyService.generateUrl({ view: QueriesHubConstants.FavoritesTab });
        this._allUrl = historyService.generateUrl({ view: QueriesHubConstants.AllQueriesTab });

        const pageHistoryService = this.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const historyState = pageHistoryService.getProjectedState();
        this._isFolderView = historyState["view"] === "folder";
        this._pivot = new ObservableValue(historyState["view"] || "");
        pageHistoryService.registerObservable(this._pivot, "view", HistoryBehavior.replace);

        this.state = {
            tabs: this._getTabs()
        };

        this._layoutManager = context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");

        this._listService = this.context.pageContext.getService<IQueryListService>("IQueryListService");
        this._listService.getQueries();
        this._loadFolderItems();

        this._addCommands();

        this._filter = new Filter();
        this._filter.subscribe(this._onUpdateFilter, FILTER_CHANGE_EVENT);
        pageHistoryService.registerFilter(this._filter, "queries", [
            {
                dataType: "string",
                filterKey: QueriesHubConstants.KeywordFilterKey,
                historyBehavior: HistoryBehavior.replace
            }
        ]);
    }

    public componentDidMount() {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(QueriesHubConstants.Area, QueriesHubConstants.FavoriteTabViewed);

        this._shortcutService.registerShortcut(Resources.QueriesHubHeader, {
            combos: ["c q"],
            description: Resources.NewQueryCommand,
            globalCombos: ["c q"],
            action: () => {
                this._onNewQuery();
            }
        });

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(
            WITCustomerIntelligenceArea,
            WITCustomerIntelligenceFeature.NEW_BOARDS_PLATFORM,
            {
                source: "Queries",
            }
        );
    }

    public componentWillUnmount(): void {
        this._filter.unsubscribe(this._onUpdateFilter);
        this._shortcutService.removeShortcutGroup(Resources.QueriesHubHeader);
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="queries-page flex-grow flex-colum" selectedTabId={this._pivot} tabProviders={this.state.tabs}>
                    <Header
                        title={!this._isFolderView && Resources.QueriesHubHeader}
                        titleSize={TitleSize.Large}
                        commandBarClassName="queries-header-commandbar"
                        commandBarItems={this._commands}
                    >
                        {this._isFolderView && <QueryBreadcrumb />}
                    </Header>
                    <div className="bolt-tabbar flex-row queries-tab-bar">
                        {!this._isFolderView ? (
                            <TabBar className="flex-grow" tabSize={TabSize.Tall} onSelectedTabChanged={this._onTabChanged} />
                        ) : (
                            <div className="flex-grow" />
                        )}
                        <FilterBar className="queries-hub-filter-bar" filter={this._filter}>
                            <KeywordFilterBarItem
                                key={QueriesHubConstants.KeywordFilterKey}
                                filterItemKey={QueriesHubConstants.KeywordFilterKey}
                                placeholder={Resources.FilterPlaceholder}
                            />
                        </FilterBar>
                    </div>
                    {this._isFolderView ? (
                        <div className="page-content page-content-top">
                            <Observer
                                items={
                                    {
                                        observableValue: this._listService.queryFolderItem,
                                        filter: () => {
                                            this._loadFolderItems();
                                            return true;
                                        }
                                    } as IObservableExpression<QueryHierarchyItem | undefined>
                                }
                            >
                                {() => {
                                    return (
                                        <FilteredQueriesTab
                                            filter={this._filter}
                                            filteredQueryItems={this._filteredQueryItems}
                                            hasMoreResults={this._hasMoreResults}
                                            isLoading={this._isLoading}
                                            loadFolderItems={this._loadFolderItems}
                                            onUpdateFilter={this._onUpdateFilter}
                                        />
                                    );
                                }}
                            </Observer>
                        </div>
                    ) : (
                        <TabContent />
                    )}
                    <Observer isDialogOpen={this._listService.isDialogOpen} dialogProps={this._listService.saveQueryDialogProps}>
                        {(props: { isDialogOpen: boolean; dialogProps: IQuerySaveDialogProps }) => {
                            return props.isDialogOpen ? <QuerySaveDialog {...props.dialogProps}></QuerySaveDialog> : null;
                        }}
                    </Observer>
                </Page>
            </Surface>
        );
    }

    private _loadFolderItems = () => {
        const folderItem = this._listService.queryFolderItem.value;
        if (this._isFolderView && folderItem && folderItem.children) {
            this._folderQueryItems = folderItem.children.map(child => ({
                ...child,
                depth: 0,
                isFavoriteObservable: new ObservableValue<boolean>(this.isItemFavorite(child)),
                isEmptyFolderContext: false,
                isLoadingContext: false
            }));
            this._folderQueryItems = this._folderQueryItems.sort((a: QueryItem, b: QueryItem): number => {
                if (a.isFolder === b.isFolder) {
                    return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 0;
                } else {
                    return a.isFolder ? -1 : 1;
                }
            });
            this._filteredQueryItems.value = [...this._folderQueryItems];
        }
    };

    private _onNewQuery = () => {
        const folderId = this._isFolderView ? this._listService.queryFolderItem.value!.id : this._listService.rootMyQueriesFolderId;
        this._listService.onEditorClick(this._pivot.value, true, folderId);
    };

    private fullScreenButton: IHeaderCommandBarItem = {
        ariaLabel: Resources.EnterFullScreen,
        iconProps: { iconName: "FullScreen" },
        id: "full-screen",
        important: false,
        text: Resources.EnterFullScreen,
        onActivate: () => {
            const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.toggleFullScreenMode();
            if (layoutManager.getFullScreenMode().value) {
                this.fullScreenButton.iconProps = { iconName: "BackToWindow" };
                this.fullScreenButton.ariaLabel = Resources.ExitFullScreen;
                this.fullScreenButton.text = Resources.ExitFullScreen;
            } else {
                this.fullScreenButton.iconProps = { iconName: "FullScreen" };
                this.fullScreenButton.ariaLabel = Resources.EnterFullScreen;
                this.fullScreenButton.text = Resources.EnterFullScreen;
            }
            this._addCommands();
        },
        subtle: true
    };

    private _addCommands() {
        this._commands.value = [
            {
                id: "new-query",
                text: Resources.NewQueryCommand,
                iconProps: { iconName: "CalculatorAddition" },
                important: true,
                isPrimary: true,
                onActivate: this._onNewQuery
            }
        ];
        if ((this._pivot && this._pivot.value && this._pivot.value === QueriesHubConstants.AllQueriesTab) || this._isFolderView) {
            this._commands.push({
                id: "new-folder",
                text: Resources.NewFolder,
                iconProps: { iconName: "FabricNewFolder" },
                onActivate: () =>
                    this._listService.onNewFolderClick(this._pivot.value, this._isFolderView ? this._listService.queryFolderItem.value : undefined)
            });
        }
        this._commands.push(this.fullScreenButton);
        this._commands.push({
            id: "import-csv",
            text: Resources.ImportWorkItems,
            iconProps: { iconName: "Upload" },
            onActivate: this._onImportCSVClick
        });
        if (isFeatureFlagEnabled(this.context.pageContext, "WebAccess.WorkItemTracking.GetDeletedQueries", false)) {
            this._commands.push({
                id: "view-deleted",
                text: Resources.DeletedQueries,
                iconProps: { iconName: "Delete" },
                important: false,
                subtle: true,
                onActivate: this._onViewDeletedQueriesClicked
            });
        }
    }

    private _applyAllFilter = async (searchText: string) => {
        const result = await this._listService.searchQueries(searchText);
        this._hasMoreResults.value = result!.hasMore;
        const filteredItems: QueryItem[] | undefined = result?.value.map(item => {
            return {
                ...item,
                depth: 0,
                isFavoriteObservable: new ObservableValue<boolean>(this.isItemFavorite(item)),
                isEmptyFolderContext: false,
                isLoadingContext: false
            };
        });

        if (filteredItems) {
            this._filteredQueryItems.value = filteredItems;
        }
    };

    private isItemFavorite = (item: QueryHierarchyItem): boolean => {
        const group = this._listService.favoriteGroups.value.find(group => {
            return group.id === QueriesHubConstants.MyFavoritesGroupKey;
        });
        return !!group?.items.find(queryItem => {
            return queryItem.id === item.id;
        });
    };

    private _applyFavoritesFilter = (searchText: string) => {
        var filteredGroupItems: { [key: string]: QueryItem[] } = {};
        const filteredItems = this._listService.favoriteGroups.value
            .filter((group: ObservableFavoriteItemsGroup) => {
                const items = group.items.filter(item => {
                    return item.name.toLowerCase().indexOf(searchText) > -1;
                });
                filteredGroupItems[group.id] = [...items];
                return items.length > 0;
            })
            .map(group => {
                return { ...group, items: filteredGroupItems[group.id] };
            });
        this._filteredQueryGroupItems.splice(0, this._filteredQueryGroupItems.length, ...filteredItems);
    };

    private _getTabs = (): IReadonlyObservableArray<IVssContributedTab> => {
        const tabs = new ObservableCollection<IVssContributedTab>();
        tabs.push([
            {
                name: Resources.FavoritesTabName,
                url: this._favoritesUrl,
                id: "favorites",
                order: 1,
                render: () => (
                    <div className="page-content page-content-top">
                        <FavoriteQueriesTab
                            isFiltered={this._isFiltered}
                            isLoading={this._isLoading}
                            filteredFavoriteGroups={this._filteredQueryGroupItems}
                            onUpdateFilter={this._onUpdateFilter}
                        />
                    </div>
                )
            },
            {
                id: "all",
                name: Resources.AllTabName,
                order: 2,
                url: this._allUrl,
                render: () => (
                    <div className="page-content page-content-top">
                        <Observer isFiltered={this._isFiltered}>
                            {(observableProps: { isFiltered: boolean }) => {
                                if (observableProps.isFiltered) {
                                    return (
                                        <FilteredQueriesTab
                                            filter={this._filter}
                                            filteredQueryItems={this._filteredQueryItems}
                                            hasMoreResults={this._hasMoreResults}
                                            isLoading={this._isLoading}
                                            loadFolderItems={this._loadFolderItems}
                                            onUpdateFilter={this._onUpdateFilter}
                                        />
                                    );
                                }
                                return <AllQueriesTab />;
                            }}
                        </Observer>
                    </div>
                )
            }
        ] as IVssContributedTab[]);

        return tabs;
    };

    private _applyFolderFilter = (searchText: string) => {
        this._filteredQueryItems.value = this._folderQueryItems.filter(item => item.name.toLowerCase().indexOf(searchText) > -1);
        this._isLoading.value = false;
    };

    private _onUpdateFilter = () => {
        const filterState = this._filter.getState();
        const searchText = filterState[QueriesHubConstants.KeywordFilterKey]?.value.toLowerCase();
        if (searchText && searchText !== "") {
            this._isLoading.value = true;
            this._isFiltered.value = true;
            if (this._isFolderView) {
                this._applyFolderFilter(searchText);
            } else if (this._pivot.value === QueriesHubConstants.AllQueriesTab) {
                this._applyAllFilter(searchText);
            } else {
                this._applyFavoritesFilter(searchText);
            }
            this._announceSearchResults();
        } else {
            this._isFiltered.value = false;
            if (this._isFolderView) {
                this._filteredQueryItems.value = [...this._folderQueryItems];
            } else {
                this._filteredQueryItems.splice(0, this._filteredQueryItems.length);
                this._filteredQueryGroupItems.splice(0, this._filteredQueryGroupItems.length);
            }
        }
    };

    private _announceSearchResults = () => {
        const text = this._getTextToAnnounce();
        announce(text);
    };

    private _getTextToAnnounce = (): string => {
        if (this._filteredQueryGroupItems.length > 0) {
            return format(
                Resources.FilteredItemsAnnouncement,
                this._filteredQueryGroupItems.length,
                this._filteredQueryGroupItems.length > 1 ? Resources.FilteredItemsAnnouncementPlural : "",
                this._getTabNameOrTextForEmptyTab()
            );
        }
        return this._getTabNameOrTextForEmptyTab(true);
    };

    private _getTabNameOrTextForEmptyTab = (empty: boolean = false): string => {
        switch (this._pivot.value) {
            case QueriesHubConstants.AllQueriesTab:
                return empty ? Resources.NoQueryResultsText : Resources.AllTabName;
            case QueriesHubConstants.FavoritesTab:
                return empty ? Resources.NoFavoriteQueriesText : Resources.FavoritesTabName;
            default:
                return this._pivot.value;
        }
    };

    private _onImportCSVClick = () => {
        const url = getImportWorkItemsUrl(this.context.pageContext);
        onClickFPS(this.context.pageContext, url, true);
    };

    private _onTabChanged = (tabKey: string) => {
        this._pivot.value = tabKey;
        this._addCommands();
        this._onUpdateFilter();
    };

    private _onViewDeletedQueriesClicked = () => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService &&
            telemetryService.publishEvent("bolt-new-queries", "BoltNewQueries.QueryAction", {
                key: "view-deleted",
                pivot: this._pivot.value,
                source: "command-button"
            });

        const url = getDeletedUrl(this.context.pageContext);
        onClickFPS(this.context.pageContext, url, true);
    };
}

Components.add("query-list-view-component", QueryListViewComponent);
